import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Shield, 
    Calendar,
    BookOpen,
    Users,
    User,
    Play,
    Square,
    Clock,
    Activity,
    Zap,
    LogOut,
    Sun,
    Moon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import NotificationCenter from '../components/NotificationCenter';
import ClockOnModal from '../components/ClockOnModal';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberHubPage() {
    const navigate = useNavigate();
    const { user, hasUnitAccess, hasGroupAccess, logout, loading: authLoading } = useAuth();
    
    // Regular attendance state
    const [myAttendance, setMyAttendance] = useState(null);
    const [clockLoading, setClockLoading] = useState(false);
    const [clockedInDuration, setClockedInDuration] = useState('');
    
    // Duty Officer attendance state (independent)
    const [myDutyOfficerAttendance, setMyDutyOfficerAttendance] = useState(null);
    const [dutyOfficerLoading, setDutyOfficerLoading] = useState(false);
    const [dutyOfficerDuration, setDutyOfficerDuration] = useState('');
    
    // Other state
    const [dataLoading, setDataLoading] = useState(true);
    const [showClockOnModal, setShowClockOnModal] = useState(false);
    const [rolePermissions, setRolePermissions] = useState({});
    
    // Theme state
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const saved = localStorage.getItem('theme');
        return saved ? saved === 'dark' : true; // Default to dark
    });
    
    // Apply theme on mount and change
    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add('dark');
            document.documentElement.classList.remove('light');
            localStorage.setItem('theme', 'dark');
        } else {
            document.documentElement.classList.remove('dark');
            document.documentElement.classList.add('light');
            localStorage.setItem('theme', 'light');
        }
    }, [isDarkMode]);
    
    const toggleTheme = () => {
        setIsDarkMode(!isDarkMode);
    };
    
    useEffect(() => {
        if (user && !authLoading) {
            fetchData();
        }
    }, [user, authLoading]);
    
    // Update regular clocked-in duration
    useEffect(() => {
        if (!myAttendance?.clock_in) {
            setClockedInDuration('');
            return;
        }
        
        const updateDuration = () => {
            const clockIn = new Date(myAttendance.clock_in);
            const now = new Date();
            const diff = now - clockIn;
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            setClockedInDuration(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        };
        
        updateDuration();
        const interval = setInterval(updateDuration, 1000);
        return () => clearInterval(interval);
    }, [myAttendance]);
    
    // Update duty officer duration
    useEffect(() => {
        if (!myDutyOfficerAttendance?.clock_in) {
            setDutyOfficerDuration('');
            return;
        }
        
        const updateDuration = () => {
            const clockIn = new Date(myDutyOfficerAttendance.clock_in);
            const now = new Date();
            const diff = now - clockIn;
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            setDutyOfficerDuration(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        };
        
        updateDuration();
        const interval = setInterval(updateDuration, 1000);
        return () => clearInterval(interval);
    }, [myDutyOfficerAttendance]);
    
    // Refresh attendance status periodically
    useEffect(() => {
        if (!user) return;
        const interval = setInterval(() => {
            fetchActiveAttendance();
            fetchActiveDutyOfficer();
        }, 30000);
        return () => clearInterval(interval);
    }, [user]);
    
    const fetchActiveAttendance = async () => {
        try {
            const response = await axios.get(`${API_URL}/attendance/my-active-regular`);
            setMyAttendance(response.data);
        } catch (error) {
            console.error('Failed to fetch active attendance:', error);
        }
    };
    
    const fetchActiveDutyOfficer = async () => {
        try {
            const response = await axios.get(`${API_URL}/attendance/my-active-duty-officer`);
            setMyDutyOfficerAttendance(response.data);
        } catch (error) {
            console.error('Failed to fetch duty officer status:', error);
        }
    };
    
    const fetchData = async () => {
        setDataLoading(true);
        try {
            const [regularRes, dutyOfficerRes, permissionsRes] = await Promise.all([
                axios.get(`${API_URL}/attendance/my-active-regular`),
                axios.get(`${API_URL}/attendance/my-active-duty-officer`),
                axios.get(`${API_URL}/permissions/my-permissions`)
            ]);
            setMyAttendance(regularRes.data);
            setMyDutyOfficerAttendance(dutyOfficerRes.data);
            setRolePermissions(permissionsRes.data || {});
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setDataLoading(false);
        }
    };
    
    const hasPermission = (permissionKey) => {
        if (user?.section_access && user.section_access[permissionKey] === false) {
            return false;
        }
        return rolePermissions[permissionKey] !== false;
    };
    
    // Regular clock on (opens modal)
    const handleClockOnClick = () => {
        setShowClockOnModal(true);
    };
    
    // Regular clock in complete (from modal)
    const handleClockInComplete = (attendanceData) => {
        setMyAttendance(attendanceData);
    };
    
    // Regular clock out
    const handleClockOut = async () => {
        if (!myAttendance) return;
        setClockLoading(true);
        try {
            await axios.post(`${API_URL}/attendance/clock-out/${myAttendance.id}`);
            setMyAttendance(null);
            toast.success('Clocked out successfully!');
        } catch (error) {
            toast.error('Failed to clock out');
        } finally {
            setClockLoading(false);
        }
    };
    
    // Duty Officer clock in (independent)
    const handleDutyOfficerClockIn = async () => {
        setDutyOfficerLoading(true);
        try {
            const response = await axios.post(`${API_URL}/attendance/duty-officer-clock-in`);
            setMyDutyOfficerAttendance(response.data);
            toast.success('Duty Officer shift started!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start Duty Officer shift');
        } finally {
            setDutyOfficerLoading(false);
        }
    };
    
    // Duty Officer clock out (independent)
    const handleDutyOfficerClockOut = async () => {
        if (!myDutyOfficerAttendance) return;
        setDutyOfficerLoading(true);
        try {
            await axios.post(`${API_URL}/attendance/duty-officer-clock-out/${myDutyOfficerAttendance.id}`);
            setMyDutyOfficerAttendance(null);
            toast.success('Duty Officer shift ended!');
        } catch (error) {
            toast.error('Failed to end Duty Officer shift');
        } finally {
            setDutyOfficerLoading(false);
        }
    };
    
    const canAccessDutyOfficer = hasPermission('duty_officer_clockon');
    const canAccessManage = hasUnitAccess() || hasGroupAccess();
    
    if (authLoading || dataLoading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-slate-950' : 'bg-gray-100'}`}>
                <div className="text-center">
                    <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>Loading...</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className={`min-h-screen flex ${isDarkMode ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`} data-testid="member-hub-page">
            {/* Sidebar */}
            <aside className={`w-64 border-r flex flex-col ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                {/* Logo */}
                <div className={`p-5 border-b ${isDarkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                            <Zap className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <span className="font-bold text-lg">Response Hub</span>
                            <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>Management System</p>
                        </div>
                    </div>
                </div>
                
                {/* User Info */}
                <div className={`p-4 border-b ${isDarkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-semibold ${
                            isDarkMode ? 'bg-slate-700' : 'bg-gray-200'
                        }`}>
                            {user?.first_name?.[0]}{user?.last_name?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{user?.first_name} {user?.last_name}</p>
                            <p className={`text-xs truncate ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{user?.role?.replace(/_/g, ' ')}</p>
                        </div>
                    </div>
                </div>
                
                {/* Navigation */}
                <nav className="flex-1 p-3 space-y-1">
                    {canAccessManage && (
                        <button
                            onClick={() => navigate('/dashboard')}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                                isDarkMode 
                                    ? 'text-slate-300 hover:bg-slate-800 hover:text-white' 
                                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                            data-testid="menu-admin-dashboard"
                        >
                            <Activity className="w-5 h-5 text-blue-400" />
                            <span>Admin Dashboard</span>
                        </button>
                    )}
                    
                    {hasPermission('portal_calendar') && (
                        <button
                            onClick={() => navigate('/calendar')}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                                isDarkMode 
                                    ? 'text-slate-300 hover:bg-slate-800 hover:text-white' 
                                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                            data-testid="menu-calendar"
                        >
                            <Calendar className="w-5 h-5 text-violet-400" />
                            <span>Calendar</span>
                        </button>
                    )}
                    
                    {hasPermission('portal_logbook') && (
                        <button
                            onClick={() => navigate('/attendance')}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                                isDarkMode 
                                    ? 'text-slate-300 hover:bg-slate-800 hover:text-white' 
                                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                            data-testid="menu-logbook"
                        >
                            <BookOpen className="w-5 h-5 text-emerald-400" />
                            <span>Logbook</span>
                        </button>
                    )}
                    
                    {hasPermission('portal_members') && (
                        <button
                            onClick={() => navigate('/members')}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                                isDarkMode 
                                    ? 'text-slate-300 hover:bg-slate-800 hover:text-white' 
                                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                            data-testid="menu-members-directory"
                        >
                            <Users className="w-5 h-5 text-amber-400" />
                            <span>Members Directory</span>
                        </button>
                    )}
                    
                    <button
                        onClick={() => navigate('/settings')}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                            isDarkMode 
                                ? 'text-slate-300 hover:bg-slate-800 hover:text-white' 
                                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                        data-testid="menu-profile"
                    >
                        <User className="w-5 h-5 text-slate-400" />
                        <span>Profile</span>
                    </button>
                </nav>
                
                {/* Theme Toggle & Logout */}
                <div className={`p-3 border-t ${isDarkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                    <button
                        onClick={toggleTheme}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors mb-1 ${
                            isDarkMode 
                                ? 'text-slate-400 hover:bg-slate-800 hover:text-white' 
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                        data-testid="theme-toggle-btn"
                    >
                        {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                    <button
                        onClick={logout}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                            isDarkMode 
                                ? 'text-slate-400 hover:bg-slate-800 hover:text-rose-400' 
                                : 'text-gray-600 hover:bg-gray-100 hover:text-rose-500'
                        }`}
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-5 h-5" />
                        <span>Sign Out</span>
                    </button>
                </div>
            </aside>
            
            {/* Main Content */}
            <main className="flex-1 flex flex-col">
                {/* Header */}
                <header className={`h-16 border-b flex items-center justify-between px-6 ${
                    isDarkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-gray-200'
                }`}>
                    <div>
                        <h1 className="text-xl font-semibold">Welcome back, {user?.first_name}</h1>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>Ready to respond?</p>
                    </div>
                    <NotificationCenter userId={user?.id} />
                </header>
                
                {/* Content Area */}
                <div className="flex-1 p-6">
                    <div className="grid grid-cols-3 gap-6 h-full">
                        {/* Left Column - Clock Actions */}
                        <div className="col-span-2 space-y-6">
                            {/* Regular Attendance Status Card */}
                            {myAttendance ? (
                                <div className={`rounded-2xl p-6 ${
                                    isDarkMode 
                                        ? 'bg-gradient-to-br from-emerald-500/20 to-emerald-600/5 border border-emerald-500/30' 
                                        : 'bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200'
                                }`}>
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></div>
                                            <span className={`font-semibold text-lg ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Active Session</span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-6">
                                        <div>
                                            <p className={`text-sm mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Activity</p>
                                            <p className="text-xl font-medium">{myAttendance.activity_name || 'General Attendance'}</p>
                                        </div>
                                        <div>
                                            <p className={`text-sm mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Duration</p>
                                            <div className="flex items-center gap-2">
                                                <Clock className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`} />
                                                <span className="text-3xl font-mono font-bold">{clockedInDuration}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className={`rounded-2xl p-6 ${
                                    isDarkMode 
                                        ? 'bg-slate-900/50 border border-slate-800' 
                                        : 'bg-white border border-gray-200'
                                }`}>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-3 h-3 rounded-full bg-slate-500"></div>
                                        <span className={`font-semibold text-lg ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Not Clocked In</span>
                                    </div>
                                    <p className={isDarkMode ? 'text-slate-500' : 'text-gray-500'}>Click the button below to start your shift</p>
                                </div>
                            )}
                            
                            {/* Duty Officer Status Card - Independent */}
                            {canAccessDutyOfficer && myDutyOfficerAttendance && (
                                <div className={`rounded-2xl p-6 ${
                                    isDarkMode 
                                        ? 'bg-gradient-to-br from-teal-600/20 to-teal-700/5 border border-teal-600/30' 
                                        : 'bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200'
                                }`}>
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full bg-teal-500 animate-pulse"></div>
                                            <span className={`font-semibold text-lg ${isDarkMode ? 'text-teal-400' : 'text-teal-700'}`}>Duty Officer Active</span>
                                        </div>
                                        <Badge className={`px-3 py-1 ${isDarkMode ? 'bg-teal-600/20 text-teal-300 border-teal-600/30' : 'bg-teal-100 text-teal-700 border-teal-300'}`}>
                                            <Shield className="w-4 h-4 mr-2" />
                                            On Duty
                                        </Badge>
                                    </div>
                                    <div className="grid grid-cols-2 gap-6">
                                        <div>
                                            <p className={`text-sm mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Role</p>
                                            <p className="text-xl font-medium">Duty Officer</p>
                                        </div>
                                        <div>
                                            <p className={`text-sm mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Duration</p>
                                            <div className="flex items-center gap-2">
                                                <Clock className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`} />
                                                <span className="text-3xl font-mono font-bold">{dutyOfficerDuration}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Clock Action Buttons */}
                            <div className="grid grid-cols-2 gap-4">
                                {/* Regular Clock On/Off */}
                                {myAttendance ? (
                                    <Button
                                        onClick={handleClockOut}
                                        disabled={clockLoading}
                                        className="h-20 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-xl text-lg shadow-lg shadow-rose-900/30 transition-all hover:shadow-rose-900/50"
                                        data-testid="clock-out-btn"
                                    >
                                        <Square className="w-6 h-6 mr-3" />
                                        Clock Off
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleClockOnClick}
                                        disabled={clockLoading}
                                        className="h-20 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl text-lg shadow-lg shadow-orange-900/30 transition-all hover:shadow-orange-900/50"
                                        data-testid="clock-in-btn"
                                    >
                                        <Play className="w-6 h-6 mr-3" />
                                        Clock On
                                    </Button>
                                )}
                                
                                {/* Duty Officer Clock On/Off - Independent */}
                                {canAccessDutyOfficer && (
                                    myDutyOfficerAttendance ? (
                                        <Button
                                            onClick={handleDutyOfficerClockOut}
                                            disabled={dutyOfficerLoading}
                                            variant="outline"
                                            className="h-20 border-teal-600/50 bg-teal-700/10 hover:bg-teal-700/20 text-teal-300 font-semibold rounded-xl text-lg"
                                            data-testid="duty-officer-clock-off-btn"
                                        >
                                            <Square className="w-6 h-6 mr-3" />
                                            Duty Officer Off
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={handleDutyOfficerClockIn}
                                            disabled={dutyOfficerLoading}
                                            className="h-20 text-white font-semibold rounded-xl text-lg"
                                            style={{ backgroundColor: '#0F766E' }}
                                            data-testid="duty-officer-clock-on-btn"
                                        >
                                            <Shield className="w-6 h-6 mr-3" />
                                            Duty Officer On
                                        </Button>
                                    )
                                )}
                            </div>
                            
                            {/* Quick Actions */}
                            <div className="grid grid-cols-4 gap-4">
                                {canAccessManage && (
                                    <button
                                        onClick={() => navigate('/dashboard')}
                                        className={`rounded-xl p-5 flex flex-col items-center gap-3 transition-all group ${
                                            isDarkMode 
                                                ? 'bg-slate-900/60 border border-slate-800 hover:bg-slate-800/60 hover:border-slate-700' 
                                                : 'bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                        }`}
                                    >
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <Activity className="w-7 h-7 text-blue-400" />
                                        </div>
                                        <span className="text-sm font-medium">Admin Dashboard</span>
                                    </button>
                                )}
                                
                                {hasPermission('portal_calendar') && (
                                    <button
                                        onClick={() => navigate('/calendar')}
                                        className={`rounded-xl p-5 flex flex-col items-center gap-3 transition-all group ${
                                            isDarkMode 
                                                ? 'bg-slate-900/60 border border-slate-800 hover:bg-slate-800/60 hover:border-slate-700' 
                                                : 'bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                        }`}
                                    >
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500/20 to-violet-600/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <Calendar className="w-7 h-7 text-violet-400" />
                                        </div>
                                        <span className="text-sm font-medium">Calendar</span>
                                    </button>
                                )}
                                
                                {hasPermission('portal_logbook') && (
                                    <button
                                        onClick={() => navigate('/attendance')}
                                        className={`rounded-xl p-5 flex flex-col items-center gap-3 transition-all group ${
                                            isDarkMode 
                                                ? 'bg-slate-900/60 border border-slate-800 hover:bg-slate-800/60 hover:border-slate-700' 
                                                : 'bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                        }`}
                                    >
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <BookOpen className="w-7 h-7 text-emerald-400" />
                                        </div>
                                        <span className="text-sm font-medium">Logbook</span>
                                    </button>
                                )}
                                
                                {hasPermission('portal_members') && (
                                    <button
                                        onClick={() => navigate('/members')}
                                        className={`rounded-xl p-5 flex flex-col items-center gap-3 transition-all group ${
                                            isDarkMode 
                                                ? 'bg-slate-900/60 border border-slate-800 hover:bg-slate-800/60 hover:border-slate-700' 
                                                : 'bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                        }`}
                                    >
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <Users className="w-7 h-7 text-amber-400" />
                                        </div>
                                        <span className="text-sm font-medium">Members</span>
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        {/* Right Column - Status & Info */}
                        <div className="space-y-6">
                            {/* Today's Info */}
                            <div className={`rounded-2xl p-5 ${isDarkMode ? 'bg-slate-900/60 border border-slate-800' : 'bg-white border border-gray-200'}`}>
                                <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Today</h3>
                                <div className="text-center py-4">
                                    <p className="text-4xl font-bold">{new Date().toLocaleDateString('en-AU', { day: 'numeric' })}</p>
                                    <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>{new Date().toLocaleDateString('en-AU', { weekday: 'long', month: 'long', year: 'numeric' })}</p>
                                </div>
                            </div>
                            
                            {/* Quick Stats */}
                            <div className={`rounded-2xl p-5 ${isDarkMode ? 'bg-slate-900/60 border border-slate-800' : 'bg-white border border-gray-200'}`}>
                                <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Your Status</h3>
                                <div className="space-y-3">
                                    <div className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
                                        <span className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>Attendance</span>
                                        <span className={`font-medium ${myAttendance ? 'text-emerald-500' : isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                            {myAttendance ? 'Clocked In' : 'Clocked Out'}
                                        </span>
                                    </div>
                                    {canAccessDutyOfficer && (
                                        <div className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
                                            <span className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>Duty Officer</span>
                                            <span className={`font-medium ${myDutyOfficerAttendance ? 'text-teal-500' : isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                                {myDutyOfficerAttendance ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    )}
                                    <div className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
                                        <span className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>Role</span>
                                        <span className="font-medium">{user?.role?.replace(/_/g, ' ')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            
            {/* Clock-On Modal */}
            <ClockOnModal
                open={showClockOnModal}
                onOpenChange={setShowClockOnModal}
                onClockIn={handleClockInComplete}
                isDutyOfficer={false}
            />
        </div>
    );
}
